/**
 * ENG-9343 — does the condition that tripped the breaker still hold?
 *
 * A circuit-breaker pause is a deliberate stop. `agent.resume` ends one, and
 * the ticket's load-bearing requirement is that the approver is not deciding
 * "resume", they are deciding "resume DESPITE `<driver>=<count>`". This module
 * is the part that turns a `status_message` string and some sibling evidence
 * into that sentence.
 *
 * ── The verdict space is deliberately TWO values, not three ────────────────
 *
 * There is no `cleared`. It is tempting to add one — a green "safe to resume"
 * badge is exactly what an approver wants — and it would be a lie, for a
 * structural reason spelled out in the manager's own auto-resume module
 * (apps/cli/src/lib/auto-resume.ts):
 *
 *   > While paused the manager spawns nothing, so "quiet" is effectively
 *   > time-since-trip.
 *
 * A paused agent generates no restart events BECAUSE it is paused. Silence is
 * therefore not evidence of recovery; it is evidence of the pause working. Any
 * "cleared" verdict computed from the target agent alone would be reading its
 * own suppression back as health. ENG-7577 already narrowed the blind
 * time-based auto-resume to provisioning trips for precisely this reason, and
 * the resume-reconciler (ENG-6383) is class-blind only because it *proves*
 * dependency recovery rather than assuming it — proof this module does not
 * have and must not pretend to.
 *
 * So:
 *   - `still_holds` — positive evidence the driver is still live. Say so loudly.
 *   - `unproven`    — no such evidence found. NOT the same as "safe", and the
 *                     card must never render it as one.
 *
 * ── Where positive evidence actually comes from ────────────────────────────
 *
 * Not from the target agent (see above). From its NEIGHBOURS, which are not
 * paused and therefore still observable:
 *
 *  1. **Same driver, same host, other agents, recently.** Most breaker drivers
 *     are host-scoped, not agent-scoped. monster's `day-rollover=3` is the
 *     worked example: the driver is a manager on `two-tractors-host` running
 *     duplicated poll cycles (ENG-9292 — the host is pinned on a pre-ENG-9046
 *     build), so one day boundary is counted three times for EVERY busy agent
 *     on that host. horatio, sherlock, sheraz and sophie tripped on the same
 *     message. Those siblings are running, so their trips are live evidence
 *     that resuming monster buys hours, not a fix — it re-trips at the next
 *     rollover.
 *
 * ── The signal that is NOT here, and why ───────────────────────────────────
 *
 * The obvious second signal is flap history: "this agent was resumed before and
 * the same driver took it down again". It is deliberately absent, because on
 * this platform it cannot currently be counted correctly, and a flag that
 * miscounts is worse than no flag.
 *
 *  - `agent.paused` audit rows are NOT one-per-trip. One trip writes a burst:
 *    measured on two-tractors-host, horatio produced 4 rows in 3 seconds and
 *    sheraz 12 rows in 0.4 seconds. Counting rows counts bursts.
 *  - Nor are the surviving rows all distinct trips. sherlock@demo-company and
 *    sterling carry 6 and 9 `agent.paused` rows spread over days — daily
 *    re-assertions of a pause that never lifted, not repeated trips. The agent
 *    did not come back in between, so nothing flapped.
 *  - And the return leg is unrecorded. Over 336h on two-tractors-host there are
 *    19 `agent.paused` rows and ZERO `agent.activated` rows, yet four of those
 *    agents are running right now: the automatic un-pause path writes no audit
 *    row at all. Only a console-operator resume does. So a flap counter keyed on
 *    pause→activate pairs reads zero on precisely the fleet where flapping is
 *    happening.
 *
 * A flap signal therefore needs a reliable "it came back" event that does not
 * exist yet. When one does, it belongs here. Until then this module claims only
 * what it can evidence.
 *
 * The sibling signal is an observation about the past and does not prove the
 * driver is live *right now*; the wording produced here says "still live" only
 * in the sense of "has not been shown to have stopped". That is the strongest
 * honest claim available, and it is enough for the decision the approver is
 * actually making.
 *
 * Pure — no DB client, no clock of its own (callers pass `now`). The API
 * gathers the evidence; this decides what it means.
 */

/** A breaker trip parsed out of `agents.status_message`. */
export interface ParsedBreakerTrip {
  /**
   * The dominant restart reason, e.g. `day-rollover`, `mcp-presence-reaper`.
   * Null when the message does not carry a per-driver breakdown — a real case
   * (older trips, truncated messages), and the reason every consumer here is
   * null-tolerant rather than assuming a driver is always known.
   */
  driver: string | null;
  /** Restart events attributed to `driver` in the trip window. Null when unparseable. */
  count: number | null;
  /** The full per-driver breakdown, e.g. `{ 'day-rollover': 3 }`. Empty when none parsed. */
  drivers: Record<string, number>;
  /** The raw message, always preserved — the card shows it verbatim. */
  raw: string;
}

/**
 * Parse the trip detail the manager writes into `agents.status_message` via
 * POST /host/circuit-breaker/trip. Current shape (restart-breaker.ts):
 *
 *   Circuit breaker tripped: 3 restarts in 10min (day-rollover=3);
 *   most recent=day-rollover at 2026-08-21T15:07:37.027Z
 *
 * Deliberately lenient. This string is operator-facing prose that has changed
 * shape before and will again; a parser that throws (or that returns a
 * confident wrong driver) on an unrecognised variant would make the resume
 * tool fail on exactly the old trips it most needs to end. Unparseable input
 * yields `driver: null` and the raw message still reaches the card, so the
 * approver reads the truth even when this function cannot summarise it.
 */
export function parseBreakerTrip(statusMessage: string | null | undefined): ParsedBreakerTrip {
  const raw = (statusMessage ?? '').trim();
  const drivers: Record<string, number> = {};
  if (!raw) return { driver: null, count: null, drivers, raw };

  // The `(driver=N; other=M)` breakdown. Driver names are the RestartReason
  // enum — lowercase kebab — so the character class is tight enough that prose
  // elsewhere in the message cannot masquerade as a driver pair.
  for (const m of raw.matchAll(/([a-z][a-z0-9-]{2,63})=(\d{1,6})\b/g)) {
    const name = m[1];
    const rawCount = m[2];
    if (!name || !rawCount) continue;
    const n = Number(rawCount);
    if (!Number.isFinite(n)) continue;
    // Keep the LARGEST observation per driver rather than the last: the same
    // name can legitimately appear twice (the breakdown and a trailing
    // "most recent=" clause), and the breakdown is the count that tripped.
    drivers[name] = Math.max(drivers[name] ?? 0, n);
  }

  const entries = Object.entries(drivers);
  if (entries.length === 0) return { driver: null, count: null, drivers, raw };

  // Dominant driver = highest count; ties broken by name so the result is
  // deterministic (a card that renders a different driver on each read is
  // worse than one that renders an arbitrary but stable choice).
  entries.sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]));
  const top = entries[0];
  if (!top) return { driver: null, count: null, drivers, raw };
  return { driver: top[0], count: top[1], drivers, raw };
}

/** One sibling agent that tripped on the same host. Gathered by the API. */
export interface SiblingTrip {
  agentId: string;
  codeName: string;
  /** The sibling's own trip message — parsed here, not by the caller. */
  statusMessage: string | null;
  /** When the sibling's `agent_paused` alert opened (epoch ms). */
  trippedAtMs: number;
}

export interface ResumePreconditionInput {
  /** The target's current `agents.status_message`. */
  statusMessage: string | null;
  /** Other agents on the SAME host with a circuit-breaker pause in the lookback. Excludes the target. */
  siblingTrips: readonly SiblingTrip[];
  /** Evaluation time, epoch ms. Injected so this stays pure and testable. */
  nowMs: number;
  /** How far back sibling/prior evidence counts. Default 14 days. */
  lookbackMs?: number;
}

export type ResumePreconditionVerdict = 'still_holds' | 'unproven';

export interface ResumePrecondition {
  verdict: ResumePreconditionVerdict;
  /** The parsed trip, for the card's "resume despite X" line. */
  trip: ParsedBreakerTrip;
  /**
   * Human-readable evidence lines, most significant first. Empty on `unproven`
   * — and an empty list is itself the message: nothing was found, which is not
   * the same as nothing being there.
   */
  signals: string[];
}

/** 14 days. Long enough to catch a weekly driver, short enough that a resolved
 *  incident from last month does not veto today's resume forever. */
export const DEFAULT_RESUME_LOOKBACK_MS = 14 * 24 * 60 * 60 * 1_000;

/**
 * Decide whether the tripping condition still holds.
 *
 * Fails toward `still_holds` on ambiguity in exactly one place — a sibling
 * whose own message is unparseable is NOT counted, because counting it would
 * mean flagging every resume on a busy host regardless of driver, and a flag
 * that is always on is a flag nobody reads. Everywhere else the bias is the
 * other way: no evidence yields `unproven`, never a clean bill of health.
 */
export function evaluateResumePrecondition(input: ResumePreconditionInput): ResumePrecondition {
  const trip = parseBreakerTrip(input.statusMessage);
  const lookback = input.lookbackMs ?? DEFAULT_RESUME_LOOKBACK_MS;
  const cutoff = input.nowMs - lookback;
  const signals: string[] = [];

  // Without a parsed driver there is nothing to match siblings against, so no
  // positive evidence is reachable. Say `unproven` and let the card show the
  // raw message — do NOT fall back to "any sibling trip counts", which would
  // attribute an unrelated driver's trips to this pause.
  if (trip.driver) {
    const matches = input.siblingTrips.filter((s) => {
      if (s.trippedAtMs < cutoff) return false;
      const parsed = parseBreakerTrip(s.statusMessage);
      // Match on the whole breakdown, not just the sibling's dominant driver:
      // a sibling that tripped on `day-rollover=3, stale-mcp=1` is evidence
      // about day-rollover even though stale-mcp is not its headline.
      return Object.prototype.hasOwnProperty.call(parsed.drivers, trip.driver as string);
    });
    // Dedupe by AGENT, not by trip row. The signal claims "N other agents",
    // and an `agent_paused` alert row is per-incident: one sibling that tripped
    // on three separate days contributes three rows and would otherwise be
    // counted as three neighbours. Deduping here rather than in the caller
    // keeps the claim true whatever gathers the evidence.
    const byAgent = new Map<string, SiblingTrip>();
    for (const m of matches) {
      const seen = byAgent.get(m.agentId);
      if (!seen || m.trippedAtMs > seen.trippedAtMs) byAgent.set(m.agentId, m);
    }
    const distinct = [...byAgent.values()];
    if (distinct.length > 0) {
      const names = distinct
        .map((m) => m.codeName)
        .sort()
        .slice(0, 6);
      const more = distinct.length > names.length ? ` (+${distinct.length - names.length} more)` : '';
      signals.push(
        `${distinct.length} other agent${distinct.length === 1 ? '' : 's'} on this host tripped on ` +
          `\`${trip.driver}\` in the last ${Math.round(lookback / 86_400_000)} days: ${names.join(', ')}${more}. ` +
          `A driver that is tripping the agent's neighbours is a HOST-level condition — resuming this ` +
          `agent does not address it.`,
      );
    }
  }

  return { verdict: signals.length > 0 ? 'still_holds' : 'unproven', trip, signals };
}

/**
 * One-line summary for the approval card header and the audit row. Kept here
 * rather than in the Slack renderer so the audit trail and the card cannot
 * drift into describing the same decision differently.
 */
export function summariseResumePrecondition(p: ResumePrecondition): string {
  const driver =
    p.trip.driver && p.trip.count !== null
      ? `${p.trip.driver}=${p.trip.count}`
      : (p.trip.driver ?? 'unknown driver');
  return p.verdict === 'still_holds'
    ? `The condition that tripped this breaker (${driver}) STILL APPEARS TO HOLD.`
    : `No evidence found that the tripping condition (${driver}) has recurred — which is NOT proof it has cleared.`;
}

/**
 * ── The organization wall on sibling evidence (ENG-9343, CodeRabbit on #4922) ──
 *
 * The resume card's precondition evidence comes from the target agent's HOST:
 * "did this agent's neighbours trip on the same driver?" That read is scoped by
 * `host_id`, and a host can bind agents from DIFFERENT organizations. So the
 * neighbours are not automatically the caller's to see — without an explicit
 * intersection, one customer's approval card would quote another customer's
 * agent names and pause messages, and their access log would never say so.
 *
 * Lives here, as a pure function, for two reasons. It is the security decision
 * rather than the plumbing around it, so it deserves a test that can fail
 * without standing up a route and a Supabase mock. And the route file is where
 * this kind of rule goes to become untestable.
 *
 * FAIL-CLOSED by construction: a sibling contributes evidence only if its org
 * RESOLVES and is in the authorized set. An unresolvable org is a drop, not a
 * pass — losing evidence costs a weaker `unproven`, keeping it costs a
 * cross-tenant disclosure, and those are not comparable prices.
 */
export interface SiblingAgentRow {
  agent_id: string;
  code_name: string | null;
  team_id: string | null;
}

export interface AuthorizedSiblings {
  /** Agent ids cleared to contribute evidence. Everything else is dropped. */
  allowed: Set<string>;
  /** Display names for the cleared ids only — an excluded org's code_name never lands here. */
  nameById: Map<string, string>;
  /** Authorized orgs actually touched, for the access log's one-row-per-org contract. */
  orgIds: Set<string>;
  /** How many siblings were withheld, so the card can say so instead of silently thinning. */
  dropped: number;
}

export function authorizeSiblingEvidence(args: {
  siblings: SiblingAgentRow[];
  /** team_id → organization_id, as resolved by the caller. */
  teamToOrg: Map<string, string | null>;
  authorizedOrgIds: Set<string>;
}): AuthorizedSiblings {
  const allowed = new Set<string>();
  const nameById = new Map<string, string>();
  const orgIds = new Set<string>();
  let dropped = 0;

  for (const s of args.siblings) {
    const orgId = s.team_id ? (args.teamToOrg.get(s.team_id) ?? null) : null;
    if (!orgId || !args.authorizedOrgIds.has(orgId)) {
      dropped += 1;
      continue;
    }
    allowed.add(s.agent_id);
    orgIds.add(orgId);
    nameById.set(s.agent_id, s.code_name ?? s.agent_id);
  }

  return { allowed, nameById, orgIds, dropped };
}
